import { useState, useEffect, useContext } from 'react';
import { AuthContext } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Plus, Trash2, Save, Users, Percent, Shield } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

export default function Settings() {
    const { API } = useContext(AuthContext);
    const [loading, setLoading] = useState(true);

    // Users State
    const [users, setUsers] = useState([]);
    const [newUser, setNewUser] = useState({ name: '', email: '', password: '', role: 'user' });



    // Allocation Rules State
    const [rules, setRules] = useState([]);
    const [newRule, setNewRule] = useState({ account_code: '', description: '', allocations: [] });

    // Mock accounts for dropdown (should fetch from API in real implementation)
    const expenseAccounts = [
        { code: '6.1.01', name: 'Alquileres' },
        { code: '6.1.04', name: 'Servicios Básicos' },
        { code: '6.2.01', name: 'Sueldos' },
    ];

    useEffect(() => {
        loadSettings();
    }, []);

    const loadSettings = async () => {
        setLoading(true);
        try {
            const [rulesResp, usersResp] = await Promise.all([
                axios.get(`${API}/settings/allocation-rules`),
                axios.get(`${API}/settings/users`)
            ]);
            setRules(Array.isArray(rulesResp.data) ? rulesResp.data : []);
            setUsers(Array.isArray(usersResp.data) ? usersResp.data : []);
        } catch (error) {
            console.error(error);
            // Don't show error on 404/empty, just init empty
        } finally {
            setLoading(false);
        }
    };



    const handleAddUser = async (e) => {
        e.preventDefault();
        try {
            const res = await axios.post(`${API}/settings/users`, newUser);
            setUsers([...users, res.data]);
            setNewUser({ name: '', email: '', password: '', role: 'user' });
            toast.success('Usuario creado exitosamente');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Error al crear usuario');
        }
    };

    const handleRoleChange = async (userId, newRole) => {
        try {
            const res = await axios.put(`${API}/settings/users/${userId}`, { role: newRole });
            setUsers(users.map(u => u.id === userId ? { ...u, role: newRole } : u));
            toast.success('Rol actualizado');
        } catch (error) {
            toast.error('Error al actualizar rol');
        }
    };

    const handleDeleteUser = async (id) => {
        if (!window.confirm('¿Estás seguro de eliminar este usuario?')) return;
        try {
            await axios.delete(`${API}/settings/users/${id}`);
            setUsers(users.filter(u => u.id !== id));
            toast.success('Usuario eliminado');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Error al eliminar usuario');
        }
    };

    // ... (More logic for Allocation Rules to be added)

    return (
        <div className="p-8 space-y-6">
            <div>
                <h1 className="text-3xl font-bold">Configuración</h1>
                <p className="text-muted-foreground">Gestiona clientes y reglas de negocio</p>
            </div>

            <Tabs defaultValue="users" className="space-y-4">
                <TabsList>
                    <TabsTrigger value="users" className="flex items-center gap-2">
                        <Shield className="h-4 w-4" /> Usuarios
                    </TabsTrigger>

                    <TabsTrigger value="allocation" className="flex items-center gap-2">
                        <Percent className="h-4 w-4" /> Prorrateo de Gastos
                    </TabsTrigger>
                </TabsList>

                <TabsContent value="users">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <Card className="md:col-span-1 h-fit">
                            <CardHeader>
                                <CardTitle>Nuevo Usuario</CardTitle>
                                <CardDescription>Crea un nuevo acceso al sistema</CardDescription>
                            </CardHeader>
                            <CardContent>
                                <form onSubmit={handleAddUser} className="space-y-4">
                                    <div className="space-y-2">
                                        <Label>Nombre</Label>
                                        <Input
                                            value={newUser.name}
                                            onChange={e => setNewUser({ ...newUser, name: e.target.value })}
                                            required
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Email</Label>
                                        <Input
                                            type="email"
                                            value={newUser.email}
                                            onChange={e => setNewUser({ ...newUser, email: e.target.value })}
                                            required
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Contraseña</Label>
                                        <Input
                                            type="password"
                                            value={newUser.password}
                                            onChange={e => setNewUser({ ...newUser, password: e.target.value })}
                                            required
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Rol</Label>
                                        <select
                                            className="flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                                            value={newUser.role}
                                            onChange={e => setNewUser({ ...newUser, role: e.target.value })}
                                        >
                                            <option value="user">Usuario</option>
                                            <option value="admin">Administrador</option>
                                        </select>
                                    </div>
                                    <Button type="submit" className="w-full">
                                        Crear Usuario
                                    </Button>
                                </form>
                            </CardContent>
                        </Card>

                        <Card className="md:col-span-2">
                            <CardHeader>
                                <CardTitle>Usuarios del Sistema</CardTitle>
                                <CardDescription>Listado de usuarios registrados</CardDescription>
                            </CardHeader>
                            <CardContent>
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>Nombre</TableHead>
                                            <TableHead>Email</TableHead>
                                            <TableHead>Rol</TableHead>
                                            <TableHead className="text-right">Acciones</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {users.map((user) => (
                                            <TableRow key={user.id}>
                                                <TableCell className="font-medium">{user.name}</TableCell>
                                                <TableCell>{user.email}</TableCell>
                                                <TableCell>
                                                    <select
                                                        className={`text-xs border rounded px-2 py-1 ${user.role === 'admin' ? 'bg-purple-50 text-purple-800 border-purple-200' : 'bg-gray-50 text-gray-800 border-gray-200'}`}
                                                        value={user.role}
                                                        onChange={(e) => handleRoleChange(user.id, e.target.value)}
                                                    >
                                                        <option value="user">Usuario</option>
                                                        <option value="admin">Administrador</option>
                                                    </select>
                                                </TableCell>
                                                <TableCell className="text-right">
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() => handleDeleteUser(user.id)}
                                                    >
                                                        <Trash2 className="h-4 w-4 text-red-500" />
                                                    </Button>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </CardContent>
                        </Card>
                    </div>
                </TabsContent>

                <TabsContent value="allocation">
                    <Card>
                        <CardHeader>
                            <CardTitle>Reglas de Prorrateo</CardTitle>
                            <CardDescription>Crea reglas para distribuir gastos comunes automáticamente.</CardDescription>
                        </CardHeader>
                        <CardContent>
                            <div className="text-center py-8 text-muted-foreground bg-secondary/20 rounded-lg border border-dashed">
                                <Percent className="h-10 w-10 mx-auto mb-2 opacity-20" />
                                <p>Funcionalidad en desarrollo.</p>
                                <p className="text-sm">Aquí podrás definir qué % de gastos como "Electricidad" se asigna a cada cliente.</p>
                            </div>
                        </CardContent>
                    </Card>
                </TabsContent>
            </Tabs>
        </div>
    );
}
